<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { StepProgress } from '@oat-sa-private/ui-components';
    import { pxToRem, resizeObserver } from '@oat-sa-private/ui-core';
    import MoreButton from './MoreButton.svelte';
    import OverviewButton from './OverviewButton.svelte';
    import StepsFocusContainer from '../StepsFocusContainer.svelte';
    import { getTestStateStore } from '../../../../testsStateStore.js';
    import getItemViewPositions from '../getItemViewPositions.js';
    import getStep from '../stepProgressHelper.js';
    import { getTimersStore } from '../../../../timers/timersStore.js';
    import { isItemReachable } from '../nonLinearNavigationHelper.js';
    import { allItemsInPart } from '../../../../util/testPart.js';

    /**
     * Component is used to display step progress navigation for non-linear tests
     */

    /**
     * @typedef StepCreator step state generator function
     * @param {Object} item - Item object from store
     * @param {Boolean} showBookmarkState
     * @param {Number} viewPosition - item position which is shown to the user
     * @returns {Object}  step data in the format of StepProgress component
     */

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId
     * @property {Boolean} [disabled]
     * @property {Boolean} [nonLinearRestricted]
     * @property {StepCreator} [stepCreator]
     * @property {Function} onMove
     * @property {Function} onMore
     * @property {Function} onOverview
     */
    /** @type {Props} */
    let {
        serviceCallId,
        disabled = false,
        nonLinearRestricted = false,
        stepCreator = getStep,
        onMove,
        onMore,
        onOverview
    } = $props();

    // svelte-ignore state_referenced_locally
    const testStateStore = getTestStateStore(serviceCallId);
    // svelte-ignore state_referenced_locally
    const timersStore = getTimersStore(serviceCallId);

    let containerWidth = $state(0);
    let overviewButtonWidth = $state(0);
    let hasFocus = $state(false);

    let testContext = $derived($testStateStore ? testStateStore.getTestContext() : null);
    let part = $derived($testStateStore ? testStateStore.getCurrentTestPart() : null);
    let stats = $derived(part?.stats);
    let itemsInPart = $derived(part ? allItemsInPart(part) : []);
    let currentPosition = $derived(testContext && part ? testContext.itemPosition - part.position : 0);
    let itemViewPositions = $derived(part ? getItemViewPositions(part) : {});
    let stepsFitAmount = $derived(calculateStepFitQuantity(overviewButtonWidth));
    let total = $derived(stats?.total);
    let range = $derived(containerWidth && total && calculatePositionRange(total, stepsFitAmount, currentPosition));
    let stepSections = $derived.by(() => {
        void $timersStore; // $derived dependency
        const testMap = $testStateStore ? testStateStore.getTestMap() : null;
        if (range && testMap) {
            return createStepSections(part, currentPosition, range, testMap);
        }
        return [];
    });

    /**
     * Calculates amount of steps that will fit into container of a given width in px
     * @returns {Number} number of steps that will fit the given width
     */
    function calculateStepFitQuantity() {
        const oneStepWidth = 6.25;
        const advanceMargin = 1;
        const ellipsisWidth = 2.5;
        const overviewMargin = 1.375;
        const containerWidthRem =
            pxToRem(Math.trunc(containerWidth - overviewButtonWidth)) -
            ellipsisWidth * 2 -
            overviewMargin -
            advanceMargin;
        return Math.floor(containerWidthRem / oneStepWidth);
    }

    /**
     * Calculates range of positions to display
     * @param {Number} itemsTotal amount of items
     * @param {Number} fitAmount amount of items that can be displayed
     * @param {Number} current current item position
     * @returns {Object} {start: startingPosition, end: endingPosition}
     */
    function calculatePositionRange(itemsTotal, fitAmount, current) {
        if (fitAmount >= itemsTotal) {
            return { start: 0, end: itemsTotal - 1 };
        } else {
            //if the amount is even make it odd
            if (fitAmount > 0 && fitAmount % 2 === 0) {
                fitAmount--;
            }
            const epsilon = Math.floor(fitAmount / 2);
            if (current - epsilon < 0) {
                return { start: 0, end: fitAmount - 1 };
            } else if (current + epsilon > itemsTotal - 1) {
                return { start: itemsTotal - fitAmount, end: itemsTotal - 1 };
            } else {
                return { start: current - epsilon, end: current + epsilon };
            }
        }
    }

    /**
     * Takes testPart and forms sections array to be used by StepProgress
     *  @param {Object} testPart testMap's test part
     *  @param {Number} positionInPart position index within the part
     *  @param {Object} stepRange object containing start and end position indexes
     *  @param {Object} testMap
     *  @returns {Array} array of section objects compatible with StepProgress component
     */
    function createStepSections(testPart, positionInPart, stepRange, testMap) {
        let sections = [];
        let connected = testPart.sections && Object.keys(testPart.sections).length > 1;

        Object.values(testPart.sections).forEach(section => {
            const steps = [];
            let sectionPosition = section.position - testPart.position;
            let current;
            let connectorStart = false;
            let connectorEnd = false;

            if (sectionPosition > stepRange.end) {
                return;
            }

            Object.values(section.items).forEach(item => {
                // avoid testMap item mutation while creating steps
                const stepItem = structuredClone(item);

                const itemIndex = stepItem.position - testPart.position;
                if (itemIndex < stepRange.start) {
                    connectorStart = true;
                    sectionPosition++;
                    return;
                }
                if (itemIndex > stepRange.end) {
                    connectorEnd = true;
                    return;
                }
                const isTimedOut = timersStore.isPositionTimedOut(stepItem.position, testMap);
                if (positionInPart === itemIndex) {
                    current = stepItem.position;
                }
                if (nonLinearRestricted) {
                    stepItem.disabled = !isItemReachable(stepItem, itemsInPart);
                }

                steps.push(stepCreator(stepItem, true, itemViewPositions[stepItem.position], isTimedOut));
            });

            if (steps.length) {
                sections.push({
                    connected,
                    steps,
                    current,
                    sectionPosition,
                    connectorStart,
                    connectorEnd
                });
            }
        });
        return sections;
    }

    /**
     * StepProgress move event handler
     * @param {CustomEvent} e
     */
    function handleMove(e) {
        onMove?.({
            position: e.detail.key
        });
    }

    function handleWidthTrackerResize(entry) {
        window.requestAnimationFrame(() => {
            containerWidth = entry.target.getBoundingClientRect().width;
        });
    }

    function handleOverviewButtonResize(entry) {
        window.requestAnimationFrame(() => {
            overviewButtonWidth = entry.target.getBoundingClientRect().width;
        });
    }
</script>

<style>
    .width-tracker {
        @add-mixin flex-center-center;
        width: 100%;
        height: 100%;
        overflow: hidden;
        flex: 1 1 0;
        background: transparent;
        z-index: 1;
    }

    .steps-container {
        display: flex;
        /* if a11y spacing styles are inherited, Steps will have layout issues */
        letter-spacing: 0;
        word-spacing: 0;
    }
    .overview-container {
        margin-block: 0;
        margin-inline: 1.375rem 0;
    }
</style>

<div class="width-tracker" use:resizeObserver={{ callback: handleWidthTrackerResize }}>
    <div class="steps-container">
        <MoreButton active={range && range.start > 0} {disabled} {onMore} />
        {#if stepSections && stepSections.length > 0}
            <StepsFocusContainer bind:hasFocus>
                {#each stepSections as stepSection (stepSection)}
                    <StepProgress
                        {...stepSection}
                        {disabled}
                        firstFocusableKey={testContext ? testContext.itemPosition : void 0}
                        containerHasFocus={hasFocus}
                        on:move={handleMove} />
                {/each}
            </StepsFocusContainer>
        {/if}
        <MoreButton active={range && range.end < total - 1} {disabled} {onMore} />
    </div>
    <span class="overview-container" use:resizeObserver={{ callback: handleOverviewButtonResize }}>
        <OverviewButton {onOverview} {serviceCallId} {disabled} />
    </span>
</div>
